import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useApp } from "../context/AppContext";

// Iteration 1: read-only bridge to the legacy MetaGo PHP-plugin.
// Pulls cache.php from the client's site (via FTP) into Spalah PG, shows a
// table of pages. Edit/push will come in a follow-up iteration.
export default function LegacyMetagoView() {
  const { proj, activeId, apiFetch, t, dark, card, btnP, btnG, btnSm, stitle, muted, flash } = useApp();
  if (!proj) return null;

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const [pulling, setPulling] = useState(false);
  const [task, setTask] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ h1: "", title: "", descr: "", content: "" });
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/legacy-metago/status`);
      const d = await r.json();
      setStatus(d);
    } catch (e) { flash("Помилка статусу: " + e.message); }
    setStatusLoading(false);
  }, [activeId, apiFetch, flash]);

  const loadPages = useCallback(async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/legacy-metago/pages`);
      const d = await r.json();
      setPages(Array.isArray(d) ? d : []);
    } catch {}
  }, [activeId, apiFetch]);

  useEffect(() => { loadStatus(); loadPages(); }, [loadStatus, loadPages]);

  const startPull = async () => {
    if (pulling) return;
    setPulling(true);
    setTask({ phase: "starting", current: 0, total: 0 });
    try {
      const r = await apiFetch(`/api/projects/${activeId}/legacy-metago/pull`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) {
        flash("Помилка: " + (d.error || ""));
        setPulling(false);
        setTask(null);
        return;
      }
      const taskId = d.taskId;
      const poll = setInterval(async () => {
        try {
          const sR = await apiFetch(`/api/tasks/${taskId}`);
          const s = await sR.json();
          setTask({ ...(s.progress || {}), status: s.status });
          if (s.status === "completed" || s.status === "failed") {
            clearInterval(poll);
            setPulling(false);
            await loadPages();
            await loadStatus();
            flash(s.status === "completed" ? `Завантажено ${(s.progress || {}).total || 0} сторінок` : "Помилка: " + (s.error || ""));
            setTimeout(() => setTask(null), 4000);
          }
        } catch {}
      }, 1500);
    } catch (e) {
      flash("Помилка: " + e.message);
      setPulling(false);
      setTask(null);
    }
  };

  // Sends POST /push with given payload + scope. On 409 sdk_active — asks user to override.
  const doPush = useCallback(async (payload, scope, scopeLabel) => {
    const body = { ...payload, scope };
    const send = async (forceSdk) => {
      const r = await apiFetch(`/api/projects/${activeId}/legacy-metago/push`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(forceSdk ? { ...body, force_sdk: true } : body),
      });
      return { r, d: await r.json() };
    };
    setPushing(true);
    try {
      let { r, d } = await send(false);
      if (r.status === 409 && d?.error === "sdk_active") {
        const conflicts = (d.conflicts || []).join(", ");
        const ok = window.confirm(
          `⚠ Spalah SDK вже керує: ${conflicts}\n\n` +
          `Push в плагін створить дубль (SSR vs JS) — ризик ранкінгів.\n\n` +
          `Все одно опублікувати "${scopeLabel}"? (force_sdk override)`
        );
        if (!ok) { setPushing(false); return false; }
        ({ r, d } = await send(true));
      }
      // Гейт по позиціях: немає знімку → масова публікація мети заблокована.
      if (r.status === 409 && d?.error === "no_snapshot") {
        flash("⚠️ " + (d.reason || "Спочатку зніміть позиції — публікація мети заблокована."));
        return false;
      }
      const skipped = Array.isArray(d.skippedHighPosition) ? d.skippedHighPosition.length : 0;
      const skipNote = skipped ? ` · 🔴 пропущено avg≥10: ${skipped} (лише поштучно)` : "";
      if (d.ok && (d.pushed > 0 || d.total === 0)) {
        flash(d.total === 0
          ? (skipped ? `Усі сторінки avg≥10 — пропущено ${skipped} (лише поштучно)` : "Нічого публікувати")
          : `✓ Опубліковано ${d.pushed} з ${d.total} (${scopeLabel})${skipNote}`);
        await loadPages();
        await loadStatus();
        return true;
      }
      flash(`⚠ ${d.pushed || 0}/${d.total || 0} (помилок: ${d.failed || 0})${skipNote} ${d.error || ""}`);
      return false;
    } catch (e) {
      flash("Помилка: " + e.message);
      return false;
    } finally {
      setPushing(false);
    }
  }, [activeId, apiFetch, flash, loadPages, loadStatus]);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    if (!q) return pages;
    return pages.filter(p =>
      (p.url || "").toLowerCase().includes(q) ||
      (p.h1 || "").toLowerCase().includes(q) ||
      (p.title || "").toLowerCase().includes(q)
    );
  }, [pages, search]);

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ ...stitle, margin: 0 }}>🔄 MetaGo міграція</h2>
        <p style={{ ...muted, margin: "4px 0 0", fontSize: 13 }}>
          Підключення до PHP-плагіна <code>{status?.path_configured || "/think-client-service/"}</code> на сайті клієнта. Iteration 1 — лише читання (pull).
        </p>
      </div>

      {/* Status card */}
      <div style={{ ...card, padding: "14px 18px", marginBottom: 14 }}>
        {!status ? (
          <div style={{ fontSize: 13, color: t.textMuted }}>{statusLoading ? "Завантаження..." : "—"}</div>
        ) : !status.configured ? (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#ef4444", marginBottom: 6 }}>⚠ Не налаштовано</div>
            <div style={{ fontSize: 12, color: t.textMuted }}>{status.reason || "legacy_metago_key порожній"}</div>
            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 8 }}>
              Заповніть <code>projects.legacy_metago_key</code> + (опційно) <code>legacy_metago_ftp_*</code> вручну в БД щоб увімкнути для цього проекту. Дані лежать в MetaGo MySQL <code>project_configs</code>.
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            <Stat label="Домен" value={status.domain} />
            <Stat label="Шлях" value={status.path_configured} hint={status.path_detected && status.path_detected !== status.path_configured ? `detected: ${status.path_detected}` : null} />
            <Stat label="Ключ" value={status.key_valid ? "✓ валідний" : "✗ невалідний"} color={status.key_valid ? "#10b981" : "#ef4444"} />
            <Stat label="FTP" value={status.has_ftp ? "✓ є" : "✗ немає"} color={status.has_ftp ? "#10b981" : "#f59e0b"} />
            <Stat label="MetaGo ID" value={status.legacy_metago_id || "—"} />
            <Stat label="Сторінок у кеші" value={status.pages_cached} />
            {status.last_pulled_at && <Stat label="Останній pull" value={new Date(status.last_pulled_at).toLocaleString("uk-UA")} />}
          </div>
        )}
      </div>

      {/* Actions */}
      {status?.configured && (
        <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center", flexWrap: "wrap" }}>
          <button
            onClick={startPull}
            disabled={pulling || !status.has_ftp || !status.key_valid}
            style={{ ...btnP, opacity: (pulling || !status.has_ftp || !status.key_valid) ? 0.5 : 1 }}
            title={!status.has_ftp ? "FTP не налаштовано" : !status.key_valid ? "Ключ невалідний" : "Завантажити cache.php з сайту клієнта"}
          >
            {pulling ? "Завантажую..." : "🔄 Синхронізувати з MetaGo"}
          </button>

          {(status.pages_edited > 0) && (
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center", padding: "0 6px", borderLeft: `1px solid ${t.border}` }}>
              <span style={{ fontSize: 11, color: t.textMuted, marginRight: 4 }}>Опублікувати ({status.pages_edited}):</span>
              <ScopeBtn
                label="↑ Тільки мета"
                title="Опублікувати тільки h1/title/description (контент в плагіні залишиться як був)"
                disabled={pushing}
                onClick={async () => {
                  if (!window.confirm(`Опублікувати МЕТА (h1/title/desc) для ${status.pages_edited} сторінок на ${status.domain}?`)) return;
                  await doPush({ all: true }, "meta", "Тільки мета");
                }}
              />
              <ScopeBtn
                label="↑ Тільки текст"
                title="Опублікувати тільки HTML-контент (мета залишиться як була)"
                disabled={pushing}
                onClick={async () => {
                  if (!window.confirm(`Опублікувати ТЕКСТ (HTML-контент) для ${status.pages_edited} сторінок на ${status.domain}?`)) return;
                  await doPush({ all: true }, "content", "Тільки текст");
                }}
              />
              <ScopeBtn
                label="↑ Все"
                title="Опублікувати мета + контент"
                accent
                disabled={pushing}
                onClick={async () => {
                  if (!window.confirm(`УВАГА: опублікувати ВСЕ (мета + контент) для ${status.pages_edited} сторінок на ЖИВИЙ сайт ${status.domain}?`)) return;
                  await doPush({ all: true }, "all", "Все");
                }}
              />
              {pushing && <span style={{ fontSize: 11, color: t.textMuted }}>публікую...</span>}
            </span>
          )}

          {task && (
            <span style={{ fontSize: 12, color: t.textMuted }}>
              {task.phase === "ftp_connect" ? "FTP-конект..." :
                task.phase === "writing" ? `Запис ${task.current || 0}/${task.total || 0}` :
                task.phase === "done" ? `✓ ${task.ok || 0} сторінок` :
                task.phase || ""}
            </span>
          )}
        </div>
      )}

      {/* Pages table */}
      {pages.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 10, alignItems: "center" }}>
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="🔍 Пошук по URL / H1 / Title"
              style={{ flex: 1, maxWidth: 400, border: `1px solid ${t.border}`, borderRadius: 6, padding: "6px 10px", fontSize: 12, background: t.bgCard, color: t.text, fontFamily: "inherit" }}
            />
            <span style={{ fontSize: 11, color: t.textMuted }}>{filtered.length} з {pages.length}</span>
          </div>

          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto", maxHeight: "65vh", overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ position: "sticky", top: 0, background: t.bgCard, zIndex: 1 }}>
                    <Th>URL</Th>
                    <Th>H1</Th>
                    <Th>Title</Th>
                    <Th>Description</Th>
                    <Th width={70}>Контент</Th>
                    <Th width={80}>Статус</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => (
                    <tr key={p.url} style={{
                      borderBottom: `1px solid ${t.border}`,
                      background: selected === p.url ? (dark ? "rgba(99,102,241,.12)" : "rgba(99,102,241,.06)") : (i % 2 ? (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") : "transparent"),
                      cursor: "pointer",
                    }} onClick={() => setSelected(p.url === selected ? null : p.url)}>
                      <Td maxw={260} title={p.url}>{shortPath(p.url)}</Td>
                      <Td maxw={200} title={p.h1}>{trunc(p.h1, 50)}</Td>
                      <Td maxw={260} title={p.title}>{trunc(p.title, 70)}</Td>
                      <Td maxw={260} title={p.descr}>{trunc(p.descr, 70)}</Td>
                      <Td>{p.content ? `${p.content.length}` : "—"}</Td>
                      <Td><StatusBadge s={p.sync_status} /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detail panel */}
          {selected && (() => {
            const p = pages.find(x => x.url === selected);
            if (!p) return null;
            const eff = (k, lk) => p[lk] != null ? p[lk] : p[k];
            const startEdit = () => {
              setDraft({
                h1: eff("h1", "h1_local") || "",
                title: eff("title", "title_local") || "",
                descr: eff("descr", "descr_local") || "",
                content: eff("content", "content_local") || "",
              });
              setEditing(true);
            };
            const cancelEdit = () => { setEditing(false); };
            const saveDraft = async () => {
              setSaving(true);
              try {
                const r = await apiFetch(`/api/projects/${activeId}/legacy-metago/pages`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ url: p.url, ...draft }),
                });
                if (!r.ok) { const d = await r.json(); flash("Помилка: " + (d.error || "")); }
                else { setEditing(false); await loadPages(); flash("Збережено локально (не на сайті)"); }
              } catch (e) { flash("Помилка: " + e.message); }
              setSaving(false);
            };
            const dropLocal = async () => {
              if (!window.confirm("Скинути локальні правки до оригіналу з MetaGo?")) return;
              const r = await apiFetch(`/api/projects/${activeId}/legacy-metago/pages/local`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ url: p.url }),
              });
              if (r.ok) { await loadPages(); flash("Скинуто"); }
            };
            const pushOneScope = async (scope, scopeLabel, summary) => {
              if (!window.confirm(`Опублікувати "${scopeLabel}" на ${proj.domain}?\n\nURL: ${p.url}\n\n${summary}`)) return;
              await doPush({ urls: [p.url] }, scope, scopeLabel);
            };

            return (
              <div style={{ ...card, padding: "14px 18px", marginTop: 12 }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 10, gap: 8 }}>
                  <strong style={{ fontSize: 13, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.url}>{p.url}</strong>
                  {!editing && <button onClick={startEdit} style={{ ...btnSm }}>✏️ Редагувати</button>}
                  {!editing && p.sync_status === "edited" && (
                    <>
                      <button onClick={dropLocal} style={{ ...btnSm, color: "#ef4444", borderColor: "rgba(239,68,68,.3)" }}>↺ Скинути правки</button>
                      <ScopeBtn
                        label="↑ Мета"
                        title="Опублікувати h1/title/description"
                        disabled={pushing}
                        onClick={() => pushOneScope("meta", "Тільки мета", "Контент в плагіні залишиться як був.")}
                      />
                      <ScopeBtn
                        label="↑ Текст"
                        title="Опублікувати HTML-контент"
                        disabled={pushing}
                        onClick={() => pushOneScope("content", "Тільки текст", "Мета залишиться як була.")}
                      />
                      <ScopeBtn
                        label="↑ Все"
                        title="Опублікувати мета + контент"
                        accent
                        disabled={pushing}
                        onClick={() => pushOneScope("all", "Все", "Замінить мета + контент.")}
                      />
                    </>
                  )}
                  {editing && (
                    <>
                      <button onClick={cancelEdit} style={btnSm} disabled={saving}>Скасувати</button>
                      <button onClick={saveDraft} disabled={saving} style={{ ...btnP, fontSize: 11, opacity: saving ? 0.5 : 1 }}>
                        {saving ? "..." : "💾 Зберегти"}
                      </button>
                    </>
                  )}
                  <button onClick={() => { setEditing(false); setSelected(null); }} style={{ ...btnSm }}>✕</button>
                </div>

                {editing ? (
                  <>
                    <EditField label="H1" value={draft.h1} onChange={v => setDraft(d => ({ ...d, h1: v }))} />
                    <EditField label="Title" value={draft.title} onChange={v => setDraft(d => ({ ...d, title: v }))} />
                    <EditField label="Description" value={draft.descr} onChange={v => setDraft(d => ({ ...d, descr: v }))} multi />
                    <EditField label="Контент (HTML)" value={draft.content} onChange={v => setDraft(d => ({ ...d, content: v }))} multi big />
                  </>
                ) : (
                  <>
                    <DetailField label="H1" value={eff("h1", "h1_local")} edited={p.h1_local != null} />
                    <DetailField label="Title" value={eff("title", "title_local")} edited={p.title_local != null} />
                    <DetailField label="Description" value={eff("descr", "descr_local")} edited={p.descr_local != null} />
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                        Контент (HTML, {(eff("content", "content_local") || "").length} символів)
                        {p.content_local != null && <span style={{ color: "#f59e0b", marginLeft: 6 }}>● змінено локально</span>}
                      </div>
                      <div style={{ background: dark ? "#0a0a10" : "#f8f9fb", padding: 10, borderRadius: 6, fontFamily: "ui-monospace, monospace", fontSize: 11, maxHeight: 250, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {eff("content", "content_local") || <span style={{ color: t.textMuted }}>(порожньо)</span>}
                      </div>
                    </div>
                  </>
                )}
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 8 }}>
                  Pulled: {p.pulled_at ? new Date(p.pulled_at).toLocaleString("uk-UA") : "—"}
                  {p.last_pushed_at && <> · Pushed: {new Date(p.last_pushed_at).toLocaleString("uk-UA")}</>}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {pages.length === 0 && status?.configured && (
        <div style={{ ...card, textAlign: "center", padding: 30, color: t.textMuted, fontSize: 13 }}>
          Кеш порожній. Натисніть «🔄 Синхронізувати з MetaGo».
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, hint }) {
  const { t } = useApp();
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: color || t.text, marginTop: 2 }}>{value ?? "—"}</div>
      {hint && <div style={{ fontSize: 10, color: t.textMuted, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function Th({ children, width }) {
  const { t } = useApp();
  return <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.textMuted, borderBottom: `2px solid ${t.border}`, textTransform: "uppercase", whiteSpace: "nowrap", width: width || "auto" }}>{children}</th>;
}

function Td({ children, maxw, title }) {
  return <td style={{ padding: "6px 10px", maxWidth: maxw || "auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={title}>{children}</td>;
}

function DetailField({ label, value, edited }) {
  const { t } = useApp();
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>
        {label}
        {edited && <span style={{ color: "#f59e0b", marginLeft: 6 }}>● змінено</span>}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.4 }}>{value || <span style={{ color: t.textMuted }}>(порожньо)</span>}</div>
    </div>
  );
}

function EditField({ label, value, onChange, multi, big }) {
  const { t, inp } = useApp();
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
        {label} <span style={{ color: t.textMuted, fontWeight: 400 }}>({(value || "").length})</span>
      </div>
      {multi ? (
        <textarea value={value} onChange={e => onChange(e.target.value)}
          style={{ ...inp, width: "100%", minHeight: big ? 180 : 60, fontFamily: big ? "ui-monospace, monospace" : "inherit", fontSize: big ? 11 : 13, resize: "vertical" }} />
      ) : (
        <input type="text" value={value} onChange={e => onChange(e.target.value)} style={{ ...inp, width: "100%", fontSize: 13 }} />
      )}
    </div>
  );
}

function ScopeBtn({ label, title, onClick, disabled, accent }) {
  const { t, dark } = useApp();
  const base = {
    fontSize: 11,
    fontWeight: 600,
    padding: "5px 10px",
    borderRadius: 5,
    border: `1px solid ${accent ? "transparent" : t.border}`,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    transition: "filter .15s",
  };
  const variant = accent
    ? { background: "linear-gradient(135deg,#059669,#10b981)", color: "#fff" }
    : { background: dark ? "rgba(99,102,241,.10)" : "rgba(99,102,241,.08)", color: dark ? "#a5b4fc" : "#4f46e5" };
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{ ...base, ...variant }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter = "brightness(1.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.filter = "none"; }}
    >
      {label}
    </button>
  );
}

function StatusBadge({ s }) {
  const map = {
    synced: { l: "✓ синк", c: "#10b981", bg: "rgba(16,185,129,.12)" },
    edited: { l: "● змінено", c: "#f59e0b", bg: "rgba(245,158,11,.12)" },
    pushed: { l: "↑ опубл.", c: "#6366f1", bg: "rgba(99,102,241,.12)" },
  };
  const v = map[s] || { l: s || "—", c: "#888", bg: "rgba(0,0,0,.05)" };
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: v.bg, color: v.c }}>{v.l}</span>;
}

function shortPath(url) {
  if (!url) return "";
  return url.replace(/^\/\/[^/]+/, "");
}
function trunc(s, n) {
  if (!s) return <span style={{ color: "#888" }}>—</span>;
  return s.length > n ? s.slice(0, n) + "…" : s;
}
